/**
 * Centralised error types for the HTTP layer.
 *
 * SRP: Every error class has exactly one reason to exist.
 * OCP: New error subtypes can be added without modifying existing ones.
 */

/** Base class for all HTTP-layer errors. */
export class HttpClientError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'HttpClientError';
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

/** Thrown when the server returns a non-2xx status code. */
export class HttpResponseError extends HttpClientError {
    readonly status: number;
    readonly responseBody: unknown;

    constructor(status: number, message: string, responseBody?: unknown) {
        super(message);
        this.name = 'HttpResponseError';
        this.status = status;
        this.responseBody = responseBody;
    }
}

/** Thrown when the request exceeds its configured timeout. */
export class HttpTimeoutError extends HttpClientError {
    constructor(url: string, timeoutMs: number) {
        super(`Request to "${url}" timed out after ${timeoutMs}ms`);
        this.name = 'HttpTimeoutError';
    }
}

/** Thrown when the network is unavailable or the request cannot be sent. */
export class HttpNetworkError extends HttpClientError {
    constructor(message: string, readonly cause?: unknown) {
        super(message);
        this.name = 'HttpNetworkError';
    }
}

/** Thrown when all retry attempts have been exhausted. */
export class HttpMaxRetriesError extends HttpClientError {
    readonly attempts: number;

    constructor(url: string, attempts: number) {
        super(`Request to "${url}" failed after ${attempts} attempt(s)`);
        this.name = 'HttpMaxRetriesError';
        this.attempts = attempts;
    }
}
